import { useMemo } from "react"
import { DateTime } from "luxon"

import type { ContextDevice } from "types/device.types"

import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import useDevices from "helpers/hooks/useDevices"
import useOrgDeviceStatuses from "helpers/hooks/devices/useOrgDeviceStatuses"
import { hasDeviceTriggeredAlerts } from "helpers/utils/alerts"
import { getDeviceReportingStatus } from "helpers/utils/devices"
import { getSpanDevicesFromRow } from "helpers/utils/spans"
import { getTowerDevicesFromRow } from "helpers/utils/towers"

interface UseContextDevicesProps {
  /** The span or tower the devices are installed on. Without one the list is empty. */
  asset: { entity: "span" | "tower"; id: number } | undefined
  /** Left out of the list, such as the device whose page this is. */
  excludeId?: number
}

/**
 * The devices on a span or a tower, with the reporting status and alert state an asset
 * header shows. They come from the org's devices, so no line or tower has to load.
 */
const useContextDevices = ({ asset, excludeId }: UseContextDevicesProps) => {
  const { devsWRelsByIdWStates, isLoading: devicesLoading } = useDevices()
  const { statusesById, isLoading: statusesLoading } = useOrgDeviceStatuses()
  const { orgAlertsBySettingId, isLoading: alertSettingsLoading } = useAlertSettings()
  // The triggered states reach the rows through `useDevices`, whose loading leaves them out.
  const { isLoading: alertStatesLoading } = useAlertSettingsWStates()

  const entity = asset?.entity
  const id = asset?.id

  const devices = useMemo((): ContextDevice[] => {
    if (!entity || !id) return []
    const rows = Object.values(devsWRelsByIdWStates)
    const onAsset =
      entity === "span"
        ? getSpanDevicesFromRow(rows, id)
        : getTowerDevicesFromRow(rows, id)
    const now = DateTime.now()
    return onAsset
      .filter(({ device }) => device.less_id !== excludeId)
      .map(({ device, settingsStates }) => ({
        id: device.less_id,
        name: device.name,
        status: getDeviceReportingStatus(statusesById[device.less_id], now),
        hasAlert: hasDeviceTriggeredAlerts(settingsStates ?? {}, orgAlertsBySettingId),
      }))
  }, [entity, id, excludeId, devsWRelsByIdWStates, statusesById, orgAlertsBySettingId])

  // Until the alerts land every device reads as fine, and the sort would reorder them after.
  return {
    devices,
    isLoading:
      devicesLoading || statusesLoading || alertSettingsLoading || alertStatesLoading,
  }
}

export default useContextDevices
